import { applyCohortAnchoredPlanStart } from './calc/planStartMonth';
import { resolveFundingCommitmentMonths } from './calc/resolveFundingCommitment';
import { resolveDefaultFundingPct, resolveDefaultRevSharePct } from './calc/resolvePlanFunding';
import { isRevSharePctConfigured } from './calc/resolveProRataRevShare';
import {
  DEFAULTS,
  DEFAULT_FUNDING_PCT,
  DEFAULT_PLAN_MARKETING_MONTHS,
  DEFAULT_REV_SHARE_PCT,
  MARKETING_PLAN_MONTHS,
  MAX_PLAN_MARKETING_MONTHS,
  PLAN_PERIODS,
} from './constants';

function padPlanCovenantArrays(state, targetLength) {
  const planMaxExposure = [...(state.planMaxExposure || [])];
  const planMaxMonthlyDraw = [...(state.planMaxMonthlyDraw || [])];
  const planDrawAmount = [...(state.planDrawAmount || [])];

  while (planMaxExposure.length < targetLength) {
    planMaxExposure.push(null);
  }
  while (planMaxMonthlyDraw.length < targetLength) {
    planMaxMonthlyDraw.push(null);
  }
  while (planDrawAmount.length < targetLength) {
    planDrawAmount.push(null);
  }

  return {
    planMaxExposure: planMaxExposure.slice(0, targetLength),
    planMaxMonthlyDraw: planMaxMonthlyDraw.slice(0, targetLength),
    planDrawAmount: planDrawAmount.slice(0, targetLength),
  };
}

function padPlanArrays(state, targetLength) {
  const len = Math.min(MAX_PLAN_MARKETING_MONTHS, Math.max(1, targetLength));
  const marketing = [...(state.marketing || [])];
  const fundingPct = [...(state.fundingPct || [])];
  const revSharePct = [...(state.revSharePct || [])];
  const defaultFunding = resolveDefaultFundingPct(state);
  const configuredDefaultRevShare = resolveDefaultRevSharePct(state);
  const defaultRevShare = isRevSharePctConfigured(configuredDefaultRevShare)
    ? configuredDefaultRevShare
    : null;

  while (marketing.length < len) {
    marketing.push(0);
  }
  while (fundingPct.length < len) {
    fundingPct.push(defaultFunding);
  }
  while (revSharePct.length < len) {
    revSharePct.push(defaultRevShare);
  }

  return {
    ...state,
    marketing: marketing.slice(0, len),
    fundingPct: fundingPct.slice(0, len),
    revSharePct: revSharePct.slice(0, len),
    ...padPlanCovenantArrays(state, len),
  };
}

export function ensureMarketingPlanIncludesCurrentMonth(state) {
  if (!state?.marketing?.length) {
    return state;
  }

  let next = { ...state };

  if (
    next.marketing.length === PLAN_PERIODS &&
    next.firstPlanMonthIsCurrentMonth !== true
  ) {
    next = {
      ...next,
      marketing: [0, ...next.marketing],
      fundingPct: [0, ...(next.fundingPct || DEFAULTS.fundingPct.slice(1))],
      revSharePct: [
        next.revSharePct?.[0] ??
          (isRevSharePctConfigured(next.defaultRevSharePct)
            ? next.defaultRevSharePct
            : null),
        ...(next.revSharePct || DEFAULTS.revSharePct.slice(1)),
      ],
      planMaxExposure: [null, ...(next.planMaxExposure || [])],
      planMaxMonthlyDraw: [null, ...(next.planMaxMonthlyDraw || [])],
      planDrawAmount: [null, ...(next.planDrawAmount || [])],
    };
  }

  if (next.marketing.length < MARKETING_PLAN_MONTHS) {
    next = padPlanArrays(next, MARKETING_PLAN_MONTHS);
  }

  if (next.planStartYear == null) {
    next.planStartYear = DEFAULTS.planStartYear;
  }
  if (next.planStartMonth == null) {
    next.planStartMonth = DEFAULTS.planStartMonth;
  }

  return applyCohortAnchoredPlanStart(next);
}

/** Leaving manual row mode: restore top defaults on plan rows and clear manual draws. */
export function disableMarketingPlanManualMode(state) {
  const limit = resolveFundingCommitmentMonths(state);
  const defaultFunding = resolveDefaultFundingPct(state);
  const defaultRevShare = resolveDefaultRevSharePct(state);
  const marketingLength = state?.marketing?.length ?? 0;

  return {
    ...state,
    marketingPlanManualMode: false,
    fundingPct: (state?.fundingPct ?? []).map((pct, index) =>
      index < limit ? defaultFunding : pct
    ),
    revSharePct: (state?.revSharePct ?? []).map((pct, index) =>
      index < limit ? defaultRevShare : pct
    ),
    planDrawAmount: Array(marketingLength).fill(null),
  };
}

export { padPlanArrays };
